<template>
  <v-row>
    <v-col cols="5" class="email-composer-container">
      <div class="d-flex flex-column flex-50" style="width: 100%">
        <span class="section-title">Escolha uma imagem</span>
        <span class="description">
          Selecione uma imagem que seja condizente com a identidade do seu negócio ou da sua promoção. Essa imagem será a "cara" do seu e-mail.
        </span>
        <mf-image-cropper
          :mb-size-limit="10"
          :upload-image="uploadHeaderImg"
          :error-message="errorImgHeader || ''"
          :url="headerImage"
          :width="600"
          :height="200"
          show-hint
          @remove-image="deleteHeaderImage"
          @snackbar="showSnackbar"
        />
        <v-divider class="my-4" />
        <span class="section-title">Corpo do e-mail</span>
        <div class="email-body-section">
          <span class="subtitle">Título</span>
          <input-with-best-offer
            v-model="title"
            height="60px"
            class="mt-2"
            channel="email"
            field="text"
            :offer-type="selectedOfferType"
            :char-count="titleCountByOfferType[selectedOfferType]"
            :max-length="maxLengthTitle"
            :error="inputErrorTitle.length > 0"
            :error-message="inputErrorTitle"
          />
        </div>
        <div class="email-body-section">
          <span class="subtitle">Texto do corpo</span>
          <input-with-best-offer
            v-model="messageText"
            class="mt-2"
            channel="email"
            field="text"
            :offer-type="selectedOfferType"
            :char-count="charCountByOfferType[selectedOfferType]"
            :max-length="maxLength"
            :error="inputErrorMessage.length > 0"
            :error-message="inputErrorMessage"
          />
        </div>
        <div>
          <p class="subtitle">Imagem do corpo do e-mail</p>
          <mf-image-cropper
            :mb-size-limit="10"
            :upload-image="uploadBodyImage"
            :error-message="errorBodyImage || ''"
            :url="bodyImage"
            :width="2000"
            :height="2000"
            show-hint
            @remove-image="deleteBodyImage"
            @snackbar="showSnackbar"
          />

          <v-divider class="my-4" />

          <p class="subtitle">Texto do botão</p>
          <mf-textarea-emoji
            v-model="buttonText"
            hide-details
            outlined
            no-resize
            rows="2"
            :max-length="20"
            avoid-variables
            dense
            height="50"
            :error-messages="errorButtonText"
          />

          <p class="subtitle">Link do botão</p>
          <v-textarea v-model="buttonLink" placeholder="https://" outlined no-resize rows="2" dense height="40" :error-messages="errorButtonLink" />
        </div>
        <v-divider class="my-4" />
        <p class="subtitle">Rodapé</p>
        <mf-textarea-emoji
          v-model="footerText"
          hide-details
          outlined
          dense
          height="50"
          no-resize
          rows="3"
          :max-length="250"
          avoid-variables
          :error-messages="errorFooter"
        />
      </div>
      <v-divider vertical class="ml-4" />
    </v-col>
    <v-col cols="7">
      <span class="mf-subtitle--cinza-0">Inspeção</span>
      <offer-type-options-menu :selected-offer-type.sync="selectedOfferType" :invalid-offer-types="invalidOfferTypes" :items="offerTypeOptions" />
      <email-preview
        class="mt-12"
        :subject="title"
        :body="messageText"
        :button-text="buttonText"
        :footer-text="footerText"
        :button-link="buttonLink"
        :urlHeaderImage="headerImage"
        :urlBodyImage="bodyImage"
        :offer-type="selectedOfferType"
      />
    </v-col>
  </v-row>
</template>

<script>
import { channelsForm } from '@/modules/maria/campanha-da-maria-v2/mixins/channels-form'
import { charCount } from '@/modules/maria/campanha-da-maria-v2/mixins/char-count'
import { bestOfferValidations } from '@/modules/maria/campanha-da-maria-v2/mixins/best-offer-validations'
import { MUTATION_SINGLE_UPLOAD_BASE64 } from '@/graphql'
import BaseMJML from '@/modules/maria/campanha-da-maria-v2/assets/simple_email.js'
import mjml2html from 'mjml-browser'

const PROCESSED_HTML = mjml2html(BaseMJML, { minify: true }).html

export default {
  name: 'SimpleEmailComposer',
  components: {
    InputWithBestOffer: () => import('@/modules/maria/campanha-da-maria-v2/views/components/ChannelsConfiguration/ChannelSettingsForm/InputWithBestOffer.vue'),
    EmailPreview: () => import('@/modules/maria/campanha-da-maria-v2/views/components/ChannelsConfiguration/ChannelSettingsForm/EmailForm/EmailPreview.vue'),
    OfferTypeOptionsMenu: () => import('@/modules/maria/campanha-da-maria-v2/views/components/ChannelsConfiguration/OfferTypeOptionsMenu.vue'),
  },
  mixins: [channelsForm, charCount, bestOfferValidations],
  props: {
    message: {
      type: Object,
      default: () => {},
    },
    settingsId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    title: '',
    inputErrorTitle: [],
    messageText: '',
    inputErrorMessage: [],
    buttonText: '',
    buttonLink: '',
    footerText: '',
    headerImage: '',
    bodyImage: '',
    processedHtml: '',
    charCountByOfferType: {},
    titleCountByOfferType: {},
    maxLength: 2000,
    maxLengthTitle: 50,
    errorImgHeader: '',
    errorBodyImage: '',
    errorButtonText: '',
    errorButtonLink: '',
    errorFooter: '',
  }),
  computed: {
    invalidOfferTypes() {
      const invalidOfferTypes = []
      this.offerTypeOptions.forEach((option) => {
        const titleCount = this.titleCountByOfferType[option.value]
        const messageCount = this.charCountByOfferType[option.value]

        if (titleCount > this.maxLengthTitle || messageCount > this.maxLength) {
          invalidOfferTypes.push(option.value)
        }
      })

      return invalidOfferTypes
    },
  },
  watch: {
    message: {
      handler(newValue) {
        if (newValue && Object.keys(newValue).length > 0) {
          this.initializeData()
          this.createCharCount()
          this.countAllCharacters()
          this.updateProcessedHTML()
        }
      },
      immediate: true,
      deep: true,
    },
    messageText(newValue) {
      this.clearInputErrorMessage()
      this.countMessageCharacters()
      this.updateProcessedHTML()
      this.$emit('update:message', newValue)
    },
    title() {
      this.clearInputErrorTitle()
      this.countTitleCharacters()
      this.updateProcessedHTML()
      this.$emit('update:title', this.title)
    },
    buttonText() {
      this.errorButtonText = ''
      this.updateProcessedHTML()
      this.$emit('update:buttonText', this.buttonText)
    },
    buttonLink() {
      this.errorButtonLink = ''
      this.formatButtonLink()
      this.updateProcessedHTML()
      this.$emit('update:buttonLink', this.buttonLink)
    },
    footerText() {
      this.errorFooter = ''
      this.updateProcessedHTML()
      this.$emit('update:footerText', this.footerText)
    },
    headerImage() {
      this.errorImgHeader = ''
      this.updateProcessedHTML()
      this.$emit('update:headerImage', this.headerImage)
    },
    bodyImage() {
      this.errorBodyImage = ''
      this.updateProcessedHTML()
      this.$emit('update:bodyImage', this.bodyImage)
    },
    processedHtml() {
      this.$emit('update:processedHtml', this.processedHtml)
    },
  },
  mounted() {
    this.createCharCount()
  },
  methods: {
    initializeData() {
      if (this.message?.simple_email_configuration) {
        this.title = this.message.simple_email_configuration.subject || this.title || ''
        this.messageText = this.message.simple_email_configuration.text || this.messageText || ''
        this.buttonText = this.message.simple_email_configuration.click_to_call_button_text || this.buttonText || ''
        this.buttonLink = this.message.simple_email_configuration.click_to_call_button_link || this.buttonLink || ''
        this.footerText = this.message.simple_email_configuration.footer || this.footerText || ''
        this.headerImage = this.message.simple_email_configuration.header_image || this.headerImage || ''
        this.bodyImage = this.message.simple_email_configuration.image || this.bodyImage || ''
      }

      if (this.message?.processed_html) {
        this.processedHtml = this.message.processed_html || this.processedHtml || ''
      }
    },
    clearInputErrorMessage() {
      this.inputErrorMessage = []
    },
    clearInputErrorTitle() {
      this.inputErrorTitle = []
    },
    createCharCount() {
      const charCountByOfferType = {}
      const titleCountByOfferType = {}
      this.offerTypeOptions.forEach((option) => {
        charCountByOfferType[option.value] = 0
        titleCountByOfferType[option.value] = 0
      })
      this.$set(this, 'charCountByOfferType', charCountByOfferType)
      this.$set(this, 'titleCountByOfferType', titleCountByOfferType)
    },
    countAllCharacters() {
      this.countMessageCharacters()
      this.countTitleCharacters()
    },
    countMessageCharacters() {
      this.offerTypeOptions.forEach((option) => {
        this.charCountByOfferType[option.value] = this.countTextWithVariables(this.messageText, 'email', 'text', option.value)
      })
    },
    countTitleCharacters() {
      this.offerTypeOptions.forEach((option) => {
        this.titleCountByOfferType[option.value] = this.countTextWithVariables(this.title, 'email', 'text', option.value)
      })
    },
    async validInput() {
      const errors = []
      this.clearInputErrorMessage()

      this.validateMessage(errors)
      this.validateTitle(errors)
      this.validateImages(errors)
      this.validateButton(errors)
      this.validateFooter(errors)

      return errors.length === 0
    },
    validateMessage(errors) {
      this.inputErrorMessage.push(...this.validateVariables(this.messageText))

      this.offerTypeOptions.forEach((option) => {
        if (this.charCountByOfferType[option.value] > this.maxLength) {
          this.inputErrorMessage.push(`A mensagem ultrapassa o limite de ${this.maxLength} caracteres para ${option.text}.`)
          errors.push(true)
        }
      })

      if (this.hasMoreThanOneBestOffer(this.messageText)) {
        this.inputErrorMessage.push('A mensagem não pode conter mais de uma variável de melhor oferta.')
        errors.push(true)
      }
      if (this.hasInvalidBestOffer(this.messageText)) {
        this.inputErrorMessage.push('A mensagem contém uma variável de melhor oferta inválida.')
        errors.push(true)
      }
      if (!this.messageText || this.messageText.trim() === '') {
        this.inputErrorMessage.push('A mensagem não pode ser vazia.')
        errors.push(true)
      }
      if (this.messageText && this.messageText.trim() === '') {
        this.inputErrorMessage.push('Não é permitido salvar apenas espaços.')
        errors.push(true)
      }

      if (!this.hasEnoughProducts(this.messageText)) {
        this.inputErrorMessage.push(
          'Para utilizar essa quantidade de produtos na variável de melhor oferta, você precisa ter pelo menos essa quantidade de produtos no passo 2. Por favor volte e insira os produtos faltantes.'
        )
        errors.push(true)
      }

      if (!this.isPricesCorrect('email', 'text', 'offer_sales', this.messageText)) {
        this.inputErrorMessage.push(
          'Para utilizar as subvariáveis de preço você precisa configurar seus respectivos valores no passo 2. Por favor volte e preencha os campos deixados em branco ou remova os preços das suas mensagens.'
        )
        errors.push(true)
      }
    },
    validateTitle(errors) {
      this.inputErrorTitle.push(...this.validateVariables(this.title))

      this.offerTypeOptions.forEach((option) => {
        if (this.titleCountByOfferType[option.value] > this.maxLengthTitle) {
          this.inputErrorTitle.push(`O título ultrapassa o limite de ${this.maxLengthTitle} caracteres para ${option.text}.`)
          errors.push(true)
        }
      })

      if (this.hasMoreThanOneBestOffer(this.title)) {
        this.inputErrorTitle.push('O título não pode conter mais de uma variável de melhor oferta.')
        errors.push(true)
      }

      if (this.hasInvalidBestOffer(this.title)) {
        this.inputErrorTitle.push('O título contém uma variável de melhor oferta inválida.')
        errors.push(true)
      }

      if (!this.title || this.title.trim() === '') {
        this.inputErrorTitle.push('O título não pode ser vazio.')
        errors.push(true)
      }

      if (this.title && this.title.trim() === '') {
        this.inputErrorTitle.push('Não é permitido salvar apenas espaços.')
        errors.push(true)
      }

      if (!this.hasEnoughProducts(this.title)) {
        this.inputErrorTitle.push(
          'Para utilizar essa quantidade de produtos na variável de melhor oferta, você precisa ter pelo menos essa quantidade de produtos no passo 2. Por favor volte e insira os produtos faltantes.'
        )
        errors.push(true)
      }

      if (!this.isPricesCorrect('email', 'text', 'offer_sales', this.title)) {
        this.inputErrorTitle.push(
          'Para utilizar as subvariáveis de preço você precisa configurar seus respectivos valores no passo 2. Por favor volte e preencha os campos deixados em branco ou remova os preços das suas mensagens.'
        )
        errors.push(true)
      }
    },
    validateImages(errors) {
      if (!this.headerImage) {
        this.errorImgHeader = 'Adicione uma imagem de cabeçalho.'
        errors.push(true)
      }

      if (!this.bodyImage) {
        this.errorBodyImage = 'Adicione uma imagem para o corpo do e-mail.'
        errors.push(true)
      }
    },
    validateButton(errors) {
      if (!this.buttonText || this.buttonText.trim() === '') {
        this.errorButtonText = 'Adicione um texto para o botão.'
        errors.push(true)
      } else if (this.buttonText.trim() === '') {
        this.errorButtonText = 'Não é permitido salvar apenas espaços.'
        errors.push(true)
      } else if (this.buttonText.length > 20) {
        this.errorButtonText = 'O texto do botão excedeu 20 caracteres.'
        errors.push(true)
      }

      if (!this.buttonLink || this.buttonLink.trim() === '') {
        this.errorButtonLink = 'Adicione um link para o botão.'
        errors.push(true)
      } else if (this.buttonLink.trim() === '') {
        this.errorButtonLink = 'Não é permitido salvar apenas espaços.'
        errors.push(true)
      } else {
        const formattedLink = this.buttonLink.match(/^https?:\/\//i) ? this.buttonLink : 'https://' + this.buttonLink

        if (!this.isValidUrl(formattedLink)) {
          this.errorButtonLink = 'O link do botão não é válido.'
          errors.push(true)
        }
      }
    },
    validateFooter(errors) {
      if (!this.footerText || this.footerText.trim() === '') {
        this.errorFooter = 'Adicione um rodapé para o e-mail.'
        errors.push(true)
      } else if (this.footerText.trim() === '') {
        this.errorFooter = 'Não é permitido salvar apenas espaços.'
        errors.push(true)
      } else if (this.footerText.length > 250) {
        this.errorFooter = 'O rodapé excedeu 250 caracteres.'
        errors.push(true)
      }
    },
    isValidUrl(string) {
      try {
        const url = new URL(string)
        return !!url
      } catch (_) {
        return false
      }
    },
    formatButtonLink() {
      if (!this.buttonLink || this.buttonLink.trim() === '') {
        return
      }

      if (!this.buttonLink.match(/^https?:\/\//i)) {
        this.buttonLink = 'https://' + this.buttonLink
      }
    },
    async getConfiguration() {
      return {
        text: this.processedHtml,
        processed_html: this.processedHtml,
        simple_email_configuration: {
          text: this.messageText,
          amount_best_offers: null,
          subject: this.title,
          amount_best_offers_subject: null,
          header_image: this.headerImage,
          image: this.bodyImage,
          click_to_call_button_text: this.buttonText,
          click_to_call_button_link: this.buttonLink,
          footer: this.footerText,
        },
      }
    },
    async uploadHeaderImg({ base64, filename }) {
      await this.$apollo
        .mutate({
          mutation: MUTATION_SINGLE_UPLOAD_BASE64,
          variables: {
            filename,
            base64,
          },
          context: {
            uri: this.$microservicesUrls['upload'],
          },
        })
        .then(({ data: { singleUploadBase64 } }) => {
          const { url } = singleUploadBase64
          this.headerImage = url
        })
    },
    deleteHeaderImage() {
      this.headerImage = ''
    },
    async uploadBodyImage({ base64, filename }) {
      await this.$apollo
        .mutate({
          mutation: MUTATION_SINGLE_UPLOAD_BASE64,
          variables: {
            filename,
            base64,
          },
          context: {
            uri: this.$microservicesUrls['upload'],
          },
        })
        .then(({ data: { singleUploadBase64 } }) => {
          const { url } = singleUploadBase64
          this.bodyImage = url
        })
    },
    deleteBodyImage() {
      this.bodyImage = ''
    },
    showSnackbar(snackbar) {
      this.$snackbar(snackbar)
    },
    updateProcessedHTML() {
      const formatter = {
        text: this.messageText,
        click_to_call_button_link: this.buttonLink,
        click_to_call_button_text: this.buttonText,
        footer: this.footerText,
        header_image: this.headerImage,
        image: this.bodyImage,
        subject: this.title,
      }
      let processedHTML = PROCESSED_HTML
      Object.entries(formatter).forEach(([key, value]) => {
        processedHTML = processedHTML.replaceAll(`{${key}}`, value || '')
      })
      this.processedHtml = processedHTML
    },
  },
}
</script>

<style lang="scss" scoped>
.email-composer-container {
  display: flex;
  flex-direction: row;
}

.section-title {
  color: $cinza-d10;
  font-size: 16px;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: 0.4px;
}

.email-body-section {
  margin-top: 22px;
}

.description {
  color: $cinza-0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin: 8px 0px 18px 0px;
}

.subtitle {
  color: $cinza-d10;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  margin-bottom: 8px !important;
}

.helper-text {
  color: $cinza-0;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  margin-top: 4px;
  margin-bottom: 0;
}

::v-deep .file-upload.v-card.v-sheet.theme--light {
  border: none;
  box-shadow: none !important;
  padding: 0 !important;

  .v-card__text {
    padding: 0;
  }
}
</style>
